import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  eligibleExperiencesQueryOptions,
  GUIDED_EXPERIENCE_QUERY_KEYS,
} from '@/api/queries/guided-experiences';
import * as guidedExperiencesApi from '@/api/endpoints/guided-experiences';
import { useAuth } from '@/hooks/use-auth';

const SOFT_PROMPT_PREFIX = 'policy_minor_update_';

export interface UsePolicySoftPromptReturn {
  isVisible: boolean;
  experienceId: string | null;
  dismiss: () => Promise<void>;
}

export function usePolicySoftPrompt(): UsePolicySoftPromptReturn {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const { data: eligibilityData } = useQuery(
    eligibleExperiencesQueryOptions(userId)
  );

  const softPromptExperience = useMemo(() => {
    if (!eligibilityData) return null;
    return (
      eligibilityData.experiences.find((e) =>
        e.experience_id.startsWith(SOFT_PROMPT_PREFIX)
      ) ?? null
    );
  }, [eligibilityData]);

  const isVisible = softPromptExperience !== null;
  const experienceId = softPromptExperience?.experience_id ?? null;

  const dismiss = useCallback(async () => {
    if (!experienceId) return;
    await guidedExperiencesApi.startExperience(experienceId);
    await guidedExperiencesApi.dismissExperience(experienceId);
    queryClient.invalidateQueries({
      queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(userId),
    });
  }, [experienceId, queryClient, userId]);

  return {
    isVisible,
    experienceId,
    dismiss,
  };
}
